import { set, child, runTransaction, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'
import * as firebaseConnection from '../firebaseConnection'

const ROOT_NODE = 'mixedMode'

type MixedMode = {
  isActive: boolean
  selectedParticipantId: number | null
}

const mixedModeState = proxy<MixedMode>({
  isActive: false,
  selectedParticipantId: null,
})

const mixedModeInitState = proxy<{
  isActiveInitialized: boolean
  isSelectedParticipantIdInitialized: boolean
}>({
  isActiveInitialized: false,
  isSelectedParticipantIdInitialized: false,
})

// Subscribe
firebaseConnection.runWhenReady(async (connection) => {
  /** Adds a Firebase listener that triggers when mixed mode is activated/deactivated. */
  onValue(child(child(connection.room, ROOT_NODE), 'isActive'), (snapshot) => {
    mixedModeState.isActive = snapshot.val() ?? false
    mixedModeInitState.isActiveInitialized = true
  })

  /** Adds a Firebase listener that triggers when the selected participant in mixed mode changes. */
  onValue(
    child(child(connection.room, ROOT_NODE), 'selectedParticipantId'),
    (snapshot) => {
      mixedModeState.selectedParticipantId = snapshot.val()
      mixedModeInitState.isSelectedParticipantIdInitialized = true
    }
  )
})

// Actions

/**
 * Sets the selected participant in mixed mode.
 * @param participantId - The ID of the participant to be selected.
 */
const setParticipantSelectedInMixedMode = async (
  participantId: number | null
) => {
  const connection = await firebaseConnection.get()

  set(
    child(child(connection.room, ROOT_NODE), 'selectedParticipantId'),
    participantId
  ).catch((err: Error) => {
    connection.logError(
      'CHANGE_SELECTED_PARTICIPANT_IN_MIXED_MODE_AND_FLOOR',
      'Failed to change selected participant both in mixed mode and floor',
      err
    )
  })
}

/** Toggles the tray in mixed mode. */
const toggleTrayMixedMode = async () => {
  const connection = await firebaseConnection.get()

  return runTransaction(
    child(child(connection.room, ROOT_NODE), 'isActive'),
    (currentValue) => !currentValue
  ).catch((err: Error) => {
    connection.logError(
      'TOGGLE_MIXED_MODE',
      'Failed to update firebase when toggling mixed mode on/off',
      err
    )
  })
}

const useMixedMode = () => useSnapshot(mixedModeState)

const useIsMixedModeActive = () => useMixedMode().isActive

const useParticipantIdSelectedInMixedMode = () =>
  useMixedMode().selectedParticipantId

export {
  type MixedMode,
  mixedModeState,
  mixedModeInitState,
  useMixedMode,
  useIsMixedModeActive,
  useParticipantIdSelectedInMixedMode,
  toggleTrayMixedMode,
  setParticipantSelectedInMixedMode,
}
